import { Box, Button, useTheme } from '@mui/material';
import { FieldArray, useFormikContext } from 'formik';
import { Add } from 'iconsax-react';
import { type FC, type ReactElement, useContext } from 'react';

import { SlabContext } from '@core/context/SlabContext';
import type { ProjectFormikType } from '@core/formik/project';
import type { DistanceDurationPair } from '@core/types/api/DistanceDurationPair/DistanceDurationPair';
import Enums from '@core/types/api/Enums';
import type { ProductSectionProps } from '@web/components/ProductSection/ProductSection';

import LocationForm from './LocationForm';

export type PlantDistanceInfo = {
  distance: DistanceDurationPair | null;
  adjustedDuration: string | null;
};

type Props = {
  curProductsRef: ProductSectionProps['curProductsRef'];
};

const ProductsByPlantSection: FC<Props> = ({ curProductsRef }) => {
  const formikBag = useFormikContext<ProjectFormikType>();
  const theme = useTheme();
  const { userInfo } = useContext(SlabContext);

  const plants = formikBag.values.productsByPlants;

  const isMultiLocationEnabled = userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagMultiLocationEnabled,
  ]);

  return (
    <FieldArray
      name='productsByPlants'
      render={(arrayHelpers): ReactElement => {
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {plants.map((plant, index) => {
              return (
                <LocationForm
                  key={plant.plant.id || `new-location-${index}`}
                  index={index}
                  arrayHelpers={arrayHelpers}
                  curProductsRef={curProductsRef}
                />
              );
            })}

            {(isMultiLocationEnabled || plants.length === 0) && (
              <Button
                type='button'
                onClick={() =>
                  arrayHelpers.push({
                    plant: { id: null, option: null },
                    products: [],
                    plantDistance: null,
                    plantDistanceUnit: null,
                    plantDriveTime: null,
                  })
                }
                sx={{
                  textTransform: 'none',
                  color: theme.palette.SlabIndigo['700'],
                  justifyContent: 'flex-start',
                  alignSelf: 'flex-start',
                }}
                startIcon={<Add color={theme.palette.SlabIndigo['700']} />}
              >
                Add Location
              </Button>
            )}
          </Box>
        );
      }}
    />
  );
};

export default ProductsByPlantSection;
